/**
 * Task-side evidence panel (Part 7, A7.1).
 *
 * Shows the active evidence requirements for the task's project, grouped by
 * status, with a "submit" affordance per requirement. Submission types:
 *  - file/photo/signature → file upload (via attachments service)
 *  - approval/checklist/external_ref → JSON payload form
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Upload, Loader2, Link as LinkIcon, ShieldCheck, ListChecks } from 'lucide-react'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { evidenceService, type EvidenceRequirement, type Evidence } from '../../services/evidence'
import { attachmentsService } from '../../services/attachments'
import type { ApiErrorLike } from '../../utils/apiError'

interface Props {
  taskId: number
  projectId: number
}

const FILE_TYPES = new Set(['file', 'photo', 'signature'])

export function TaskEvidencePanel({ taskId, projectId }: Props) {
  const [reqs, setReqs] = useState<EvidenceRequirement[]>([])
  const [items, setItems] = useState<Evidence[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<number | null>(null)

  useEffect(() => { load() }, [taskId, projectId])

  async function load() {
    setLoading(true)
    try {
      const [r, e] = await Promise.all([
        evidenceService.listRequirements(projectId),
        evidenceService.listForTask(taskId),
      ])
      setReqs(r)
      setItems(e)
    } catch { toast.error('Failed to load evidence') }
    finally { setLoading(false) }
  }

  async function uploadFile(req: EvidenceRequirement, file: File) {
    setBusy(req.id)
    try {
      const att = await attachmentsService.upload({
        entityType: 'task', entityId: taskId, file, purpose: 'completion_evidence',
      })
      await evidenceService.submit(taskId, { requirement_id: req.id, attachment_id: att.id })
      toast.success(`${req.label} attached`)
      load()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Upload failed')
    } finally { setBusy(null) }
  }

  async function submitPayload(req: EvidenceRequirement, payload: Record<string, unknown>) {
    setBusy(req.id)
    try {
      await evidenceService.submit(taskId, { requirement_id: req.id, payload })
      toast.success(`${req.label} recorded`)
      load()
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Submit failed')
    } finally { setBusy(null) }
  }

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="size-4 animate-spin" /></div>
  if (reqs.length === 0) return null

  const satisfiedReqIds = new Set(items.map(i => i.requirement_id))

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Completion Evidence</h3>
        <span className="text-xs text-muted-foreground">{items.length} of {reqs.length} provided</span>
      </div>

      <ul className="space-y-2">
        {reqs.map(req => {
          const done = satisfiedReqIds.has(req.id)
          return (
            <li key={req.id} className="flex items-start gap-3 rounded-md border p-3">
              <div className={`size-6 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-green-500/15 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                {done ? <Check className="size-4" /> : iconFor(req.evidence_type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{req.label}</span>
                  <Badge variant="outline" className="text-xs capitalize">{req.status_name.replace('_', ' ')}</Badge>
                  {req.is_required && !done && <Badge variant="destructive" className="text-xs">Required</Badge>}
                </div>
                {req.instructions && <p className="text-xs text-muted-foreground mt-0.5">{req.instructions}</p>}

                {!done && (
                  <div className="mt-2">
                    {FILE_TYPES.has(req.evidence_type) ? (
                      <Label className="inline-flex items-center gap-2 text-xs cursor-pointer text-primary hover:underline">
                        <Upload className="size-3.5" />
                        {busy === req.id ? 'Uploading…' : 'Upload file'}
                        <input
                          type="file"
                          className="hidden"
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(req, f) }}
                        />
                      </Label>
                    ) : req.evidence_type === 'approval' ? (
                      <ApprovalForm onSubmit={p => submitPayload(req, p)} disabled={busy === req.id} />
                    ) : req.evidence_type === 'external_ref' ? (
                      <ExternalRefForm onSubmit={p => submitPayload(req, p)} disabled={busy === req.id} />
                    ) : (
                      <Button size="sm" variant="outline" disabled={busy === req.id}
                        onClick={() => submitPayload(req, { completed: true })}>
                        Mark complete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function iconFor(t: string) {
  if (t === 'approval') return <ShieldCheck className="size-3.5" />
  if (t === 'external_ref') return <LinkIcon className="size-3.5" />
  if (t === 'checklist') return <ListChecks className="size-3.5" />
  return <Upload className="size-3.5" />
}

function ApprovalForm({ onSubmit, disabled }: { onSubmit: (p: Record<string, unknown>) => void; disabled: boolean }) {
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[160px]">
        <Label className="text-xs">Approver name</Label>
        <Input value={name} onChange={e => setName(e.target.value)} className="h-8 text-sm" />
      </div>
      <div className="flex-1 min-w-[160px]">
        <Label className="text-xs">Note (optional)</Label>
        <Input value={note} onChange={e => setNote(e.target.value)} className="h-8 text-sm" />
      </div>
      <Button size="sm" disabled={disabled || !name.trim()}
        onClick={() => onSubmit({ approver: name.trim(), note: note.trim(), approved_at: new Date().toISOString() })}>
        Record approval
      </Button>
    </div>
  )
}

function ExternalRefForm({ onSubmit, disabled }: { onSubmit: (p: Record<string, unknown>) => void; disabled: boolean }) {
  const [url, setUrl] = useState('')
  const [label, setLabel] = useState('')
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[200px]">
        <Label className="text-xs">URL</Label>
        <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" className="h-8 text-sm" />
      </div>
      <div className="flex-1 min-w-[140px]">
        <Label className="text-xs">Label</Label>
        <Input value={label} onChange={e => setLabel(e.target.value)} className="h-8 text-sm" />
      </div>
      <Button size="sm" disabled={disabled || !url.trim()} onClick={() => onSubmit({ url: url.trim(), label: label.trim() })}>
        Save link
      </Button>
    </div>
  )
}
